import { useState } from 'react'

type ExportLine = { name: string; cost: number; sell: number }

const safe = (value: string) =>
  value
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/(^-|-$)/g, '')
    .toLowerCase()

export function QuoteDelivery({
  route,
  totalSell,
  totalProfit,
  lines,
}: { route: string; totalSell: number; totalProfit: number; lines: ExportLine[] }) {
  const [copied, setCopied] = useState(false)
  const [published, setPublished] = useState(false)
  const [email, setEmail] = useState('ops@astermachinery.com')
  const [invited, setInvited] = useState(false)
  const quoteRef = `Q-${safe(route).slice(0, 14).toUpperCase()}-184`
  const shareUrl = `https://offers.navo24.com/${quoteRef.toLowerCase()}`
  const message = `Freight offer ${quoteRef} for ${route}: $${Math.round(totalSell).toLocaleString('en-US')}. Review and confirm: ${shareUrl}`

  const copyLink = async () => {
    await navigator.clipboard?.writeText(shareUrl)
    setCopied(true)
  }
  const download = (format: 'csv' | 'json' | 'xls') => {
    const rows = [
      ['Charge', 'Net cost', 'Customer sell', 'Profit'],
      ...lines.map((line) => [line.name, line.cost, line.sell, line.sell - line.cost]),
    ]
    const body =
      format === 'json'
        ? JSON.stringify({ quoteRef, route, totalSell, totalProfit, lines }, null, 2)
        : rows.map((row) => row.join(format === 'xls' ? '\t' : ',')).join('\n')
    const blob = new Blob([body], {
      type: format === 'json' ? 'application/json' : 'text/plain;charset=utf-8',
    })
    const anchor = document.createElement('a')
    anchor.href = URL.createObjectURL(blob)
    anchor.download = `${quoteRef}.${format}`
    anchor.click()
    URL.revokeObjectURL(anchor.href)
  }

  return (
    <section className="qdl">
      <header>
        <div>
          <span>Share and confirmation</span>
          <h3>Send the offer. Let the customer confirm.</h3>
        </div>
        <b className={published ? 'live' : ''}>{published ? 'Offer live' : 'Private draft'}</b>
      </header>
      <div className="qdl-grid">
        <div className="qdl-link">
          <span>Secure customer link</span>
          <div>
            <code>{shareUrl}</code>
            <button type="button" onClick={copyLink}>
              {copied ? 'Copied ✓' : 'Copy link'}
            </button>
          </div>
          <small>
            Opening the link does not accept the offer. The customer must select Confirm offer.
          </small>
        </div>
        <div className="qdl-confirm">
          <span>Customer confirmation portal</span>
          <div className="qdl-flow">
            <b>Review</b>
            <i />
            <b>Confirm</b>
            <i />
            <b>Audit record</b>
          </div>
          <button type="button" onClick={() => setPublished(true)}>
            {published ? 'Portal published ✓' : 'Publish confirmation page →'}
          </button>
        </div>
      </div>
      <div className="qdl-send">
        <label>
          <span>Invite customer</span>
          <input
            aria-label="Customer offer email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <button type="button" className="primary" onClick={() => setInvited(true)}>
          {invited ? `Invited ${email} ✓` : 'Send invitation'}
        </button>
        <a
          href={`mailto:${email}?subject=${encodeURIComponent(`Freight offer ${quoteRef}`)}&body=${encodeURIComponent(message)}`}
        >
          Email
        </a>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noreferrer"
        >
          WhatsApp
        </a>
        <button type="button" onClick={copyLink}>
          WeChat
        </button>
      </div>
      <footer>
        <span>Save or attach</span>
        <button type="button" onClick={() => window.print()}>
          PDF
        </button>
        <button type="button" onClick={() => download('xls')}>
          Excel
        </button>
        <button type="button" onClick={() => download('csv')}>
          CSV
        </button>
        <button type="button" onClick={() => download('json')}>
          JSON
        </button>
        <small>Quote version 1 · link records views, comments and final confirmation.</small>
      </footer>
      <style>{styles}</style>
    </section>
  )
}

const styles = `
.qdl{border-top:1px solid var(--border);background:color-mix(in srgb,var(--surface-1) 55%,var(--surface-0))}.qdl *{box-sizing:border-box}.qdl button,.qdl input,.qdl a{font:inherit}.qdl>header{display:flex;justify-content:space-between;align-items:center;padding:14px 16px}.qdl>header span,.qdl-link>span,.qdl-confirm>span,.qdl-send label>span,.qdl>footer>span{font-family:var(--mono,ui-monospace,monospace);font-size:8px;letter-spacing:.1em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.qdl>header h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:17px;margin:3px 0 0}.qdl>header>b{font-size:8px;padding:4px 7px;border-radius:10px;background:var(--surface-1);color:var(--text-muted)}.qdl>header>b.live{background:#10b98118;color:#047857}.qdl-grid{display:grid;grid-template-columns:1.35fr .85fr;border-top:1px solid var(--border);border-bottom:1px solid var(--border)}.qdl-grid>div{padding:13px 16px}.qdl-grid>div:first-child{border-right:1px solid var(--border)}.qdl-link>div{display:flex;margin-top:7px}.qdl-link code{min-width:0;flex:1;border:1px solid var(--border);background:var(--surface-0);border-radius:8px 0 0 8px;padding:8px 9px;font-family:var(--mono,ui-monospace,monospace);font-size:8.5px;overflow:hidden;text-overflow:ellipsis}.qdl-link button{border:0;background:var(--text-primary);color:var(--surface-0);border-radius:0 8px 8px 0;padding:0 11px;font-size:8.5px;font-weight:700}.qdl-link small{display:block;color:var(--text-muted);font-size:7.5px;margin-top:5px}.qdl-flow{display:flex;align-items:center;gap:6px;margin:10px 0}.qdl-flow b{font-size:8px}.qdl-flow i{height:1px;flex:1;background:var(--border)}.qdl-confirm button{border:0;background:none;color:var(--frd-accent);font-size:8.5px;font-weight:700;padding:0}.qdl-send{display:flex;align-items:end;gap:7px;padding:12px 16px}.qdl-send label{display:grid;gap:4px;flex:1}.qdl-send input{border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:8px;padding:8px 9px;font-size:9px}.qdl-send button,.qdl-send a{border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:8px;padding:8px 10px;font-size:8.5px;font-weight:700;text-decoration:none}.qdl-send .primary{background:var(--text-primary);color:var(--surface-0);border-color:var(--text-primary)}.qdl>footer{display:flex;align-items:center;gap:6px;padding:10px 16px;border-top:1px solid var(--border)}.qdl>footer button{border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:7px;padding:6px 9px;font-size:8px;font-weight:700}.qdl>footer small{margin-left:auto;color:var(--text-muted);font-size:7.5px}
@media(max-width:760px){.qdl-grid{grid-template-columns:1fr}.qdl-grid>div:first-child{border-right:0;border-bottom:1px solid var(--border)}.qdl-send{align-items:stretch;display:grid;grid-template-columns:1fr 1fr}.qdl-send label{grid-column:1/-1}.qdl-send .primary{grid-column:1/-1}.qdl-send a,.qdl-send>button:not(.primary){text-align:center}.qdl>footer{flex-wrap:wrap}.qdl>footer>span{width:100%}.qdl>footer small{width:100%;margin:3px 0 0}}
`
